import { useSelector } from "react-redux";
import { selectSubscriptionStatus } from "../store/auth/index";
import { useUser } from "./useUser";
import { isActivePlatformSupportSession } from "../utils/platformSupportSession";

/**
 * Hook to check subscription status
 * Returns subscription data and helper functions to check subscription state
 */
export const useSubscription = () => {
  const subscription = useSelector(selectSubscriptionStatus);
  const user = useUser();
  const isSupportSession = isActivePlatformSupportSession(user);

  const isActive = () => {
    // Full-write platform support must not be blocked by coop subscription state.
    if (isSupportSession) return true;

    if (!subscription) return false;
    // Check if subscription is active based on is_active flag or expires_at date
    if (subscription.is_active !== undefined) {
      return subscription.is_active === true;
    }
    // Fallback: check if expires_at is in the future
    if (subscription.expires_at) {
      return new Date(subscription.expires_at) > new Date();
    }
    return false;
  };

  const isExpired = () => {
    if (isSupportSession) return false;
    if (!subscription) return true;
    if (subscription.is_active === false) return true;
    if (subscription.expires_at) {
      return new Date(subscription.expires_at) <= new Date();
    }
    return false;
  };

  const getWarningMessage = () => {
    if (isSupportSession) {
      if (!subscription) {
        return "Support session: this cooperative’s subscription status is unavailable. You can still work; renew the plan in Manager when ready.";
      }
      if (
        subscription.expires_at &&
        new Date(subscription.expires_at) <= new Date()
      ) {
        return "Support session: this cooperative’s subscription is expired. You can still work; renew the plan in Manager when ready.";
      }
      return null;
    }
    if (!subscription) {
      return "Your subscription status is unavailable. Please contact your organization's administrator.";
    }
    if (isExpired()) {
      return "Your subscription has expired. Please contact your organization's administrator to renew your subscription.";
    }
    if (!isActive()) {
      return "Your subscription is inactive. Please contact your organization's administrator.";
    }
    return null;
  };

  return {
    subscription,
    isActive: isActive(),
    isExpired: isExpired(),
    hasSubscription: !!subscription,
    isSupportSession,
    warningMessage: getWarningMessage(),
  };
};
